import { useEffect, useState } from "react";
import { Clock, Tag } from "lucide-react";
import { getHomepagePromotion, type HomepagePromotion } from "@/api/client";

function pad(n: number) {
  return String(n).padStart(2, "0");
}

function discountLabel(p: HomepagePromotion): string {
  if (p.discountType === "PERCENT") return `${p.discountValue}% OFF`;
  return `R$ ${p.discountValue.toFixed(0)} OFF`;
}

/**
 * Site-wide announcement bar that reflects the active homepage promotion.
 * Fetched once from /api/public/homepage-promotion. If no active promotion
 * exists, the bar is hidden entirely — nothing stale, nothing hardcoded.
 *
 * This replaces the two legacy countdowns (CountdownBanner + HeroCarousel
 * internal timer) with a single source of truth driven by the admin.
 */
export const PromotionBanner = () => {
  const [promo, setPromo] = useState<HomepagePromotion | null>(null);
  const [time, setTime] = useState({ d: 0, h: 0, m: 0, s: 0 });
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    void getHomepagePromotion()
      .then((p) => setPromo(p))
      .catch(() => setPromo(null));
  }, []);

  useEffect(() => {
    if (!promo) return;
    const end = new Date(promo.endsAt).getTime();

    function tick() {
      const diff = end - Date.now();
      if (diff <= 0) {
        setExpired(true);
        return;
      }
      const d = Math.floor(diff / 86_400_000);
      const h = Math.floor((diff % 86_400_000) / 3_600_000);
      const m = Math.floor((diff % 3_600_000) / 60_000);
      const s = Math.floor((diff % 60_000) / 1_000);
      setTime({ d, h, m, s });
    }
    tick();
    const id = setInterval(tick, 1_000);
    return () => clearInterval(id);
  }, [promo]);

  if (!promo || expired) return null;

  return (
    <div className="bg-gradient-brand text-white">
      <div className="container flex flex-col items-center justify-center gap-2 py-3 sm:flex-row sm:gap-5">
        <div className="flex items-center gap-2">
          <Tag className="h-4 w-4 shrink-0" />
          {promo.badgeLabel && (
            <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider">
              {promo.badgeLabel}
            </span>
          )}
          <span className="text-sm font-semibold">
            {promo.name} · <strong>{discountLabel(promo)}</strong>
          </span>
        </div>

        <div className="flex items-center gap-1.5 font-mono text-sm font-bold">
          <Clock className="h-3.5 w-3.5" />
          <span className="text-xs opacity-80">Termina em</span>
          {time.d > 0 && (
            <>
              <span className="rounded bg-white/20 px-2 py-0.5 tabular-nums">{pad(time.d)}d</span>
              <span className="opacity-60">:</span>
            </>
          )}
          <span className="rounded bg-white/20 px-2 py-0.5 tabular-nums">{pad(time.h)}h</span>
          <span className="opacity-60">:</span>
          <span className="rounded bg-white/20 px-2 py-0.5 tabular-nums">{pad(time.m)}m</span>
          <span className="opacity-60">:</span>
          <span className="rounded bg-white/20 px-2 py-0.5 tabular-nums">{pad(time.s)}s</span>
        </div>

        <a
          href="/produtos"
          className="rounded-full bg-white text-primary px-3 py-1 text-xs font-bold shadow-sm hover:bg-white/90 transition-colors"
        >
          Aproveitar →
        </a>
      </div>
    </div>
  );
};
